import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api } from '../utils/api'

function OrderConfirmation() {
  const { orderId } = useParams()
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadOrder()
  }, [orderId])

  const loadOrder = async () => {
    try {
      const orderData = await api.getOrder(orderId)
      setOrder(orderData)
    } catch (error) {
      console.error('Failed to load order:', error)
    } finally {
      setLoading(false)
    }
  }

  const generatePDF = () => {
    window.print()
  }

  const downloadInvoice = () => {
    const invoiceContent = generateInvoiceContent()
    const blob = new Blob([invoiceContent], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `invoice-${order.orderNumber}.html`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const generateInvoiceContent = () => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${order.orderNumber}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
          .details { margin: 20px 0; }
          .table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          .table th, .table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
          .table th { background-color: #f5f5f5; }
          .totals { float: right; margin-top: 20px; }
          .thank-you { margin-top: 30px; text-align: center; padding-top: 20px; border-top: 2px solid #333; }
          .total-row { margin: 5px 0; }
          .grand-total { font-weight: bold; font-size: 1.2em; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>INVOICE</h1>
          <p><strong>Order #:</strong> ${order.orderNumber}</p>
          <p><strong>Date:</strong> ${new Date(order.orderDate).toLocaleDateString()}</p>
        </div>
        
        <div class="details">
          <h3>Customer Details</h3>
          <p><strong>Name:</strong> ${order.customer.firstName} ${order.customer.lastName}</p>
          <p><strong>Email:</strong> ${order.customer.email}</p>
          <p><strong>Phone:</strong> ${order.customer.phone}</p>
          <p><strong>Address:</strong> ${order.customer.address}</p>
          <p><strong>City:</strong> ${order.customer.city}, ${order.customer.state} ${order.customer.zipCode}</p>
          <p><strong>Country:</strong> ${order.customer.country}</p>
        </div>
        
        <table class="table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Quantity</th>
              <th>Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${order.items.map(item => `
              <tr>
                <td>${item.name}</td>
                <td>${item.quantity}</td>
                <td>$${item.price.toFixed(2)}</td>
                <td>$${(item.price * item.quantity).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="totals">
          <div class="total-row">Subtotal: $${order.subtotal.toFixed(2)}</div>
          <div class="total-row">Delivery: $${order.deliveryCharge.toFixed(2)}</div>
          <div class="total-row">Tax: $${order.tax.toFixed(2)}</div>
          <div class="total-row grand-total">Total: $${order.total.toFixed(2)}</div>
        </div>
        
        <div class="thank-you">
          <p><strong>Thank you for your order!</strong></p>
          <p>ShopCloud E-commerce</p>
        </div>
      </body>
      </html>
    `
  }

  if (loading) {
    return <div className="container">Loading order details...</div>
  }

  if (!order) {
    return (
      <div className="container">
        <h1>Order Not Found</h1>
        <p>The order you're looking for doesn't exist.</p>
        <Link to="/" className="btn">Return to Home</Link>
      </div>
    )
  }

  return (
    <div className="container">
      <div className="order-confirmation">
        <div className="confirmation-header">
          <h1>Order Confirmed!</h1>
          <p className="order-number">Order #: {order.orderNumber}</p>
          <p className="order-date">Date: {new Date(order.orderDate).toLocaleDateString()}</p>
        </div>

        <div className="confirmation-details">
          <div className="customer-info">
            <h2>Customer Information</h2>
            <p><strong>Name:</strong> {order.customer.firstName} {order.customer.lastName}</p>
            <p><strong>Email:</strong> {order.customer.email}</p>
            <p><strong>Phone:</strong> {order.customer.phone}</p>
            <p><strong>Address:</strong> {order.customer.address}</p>
            <p><strong>City:</strong> {order.customer.city}</p>
            <p><strong>State:</strong> {order.customer.state}</p>
            <p><strong>ZIP Code:</strong> {order.customer.zipCode}</p>
            <p><strong>Country:</strong> {order.customer.country}</p>
          </div>

          <div className="order-info">
            <h2>Order Details</h2>
            <div className="order-items">
              {order.items.map(item => (
                <div key={item.id} className="order-item">
                  <span>{item.name} × {item.quantity}</span>
                  <span>${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            
            <div className="order-totals">
              <div className="total-row">
                <span>Subtotal:</span>
                <span>${order.subtotal.toFixed(2)}</span>
              </div>
              <div className="total-row">
                <span>Delivery ({order.deliveryMethod}):</span>
                <span>${order.deliveryCharge.toFixed(2)}</span>
              </div>
              <div className="total-row">
                <span>Tax:</span>
                <span>${order.tax.toFixed(2)}</span>
              </div>
              <div className="total-row grand-total">
                <span>Total:</span>
                <span>${order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="confirmation-actions">
          <button onClick={generatePDF} className="btn">
            Print Invoice
          </button>
          <button onClick={downloadInvoice} className="btn btn-secondary">
            Download Invoice
          </button>
          <Link to="/" className="btn">
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  )
}

export default OrderConfirmation
